import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Loading } from '../components/common';
import './CrawlPage.css';

type DataSource = 'roofstock' | 'rentcast' | 'zillow' | 'zumper';

interface CrawlStatus {
  source: DataSource;
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  lastRun?: string;
  stats?: {
    total?: number;
    pending?: number;
    completed?: number;
    failed?: number;
  };
}

export function CrawlPage() {
  const [activeSource, setActiveSource] = useState<DataSource>('roofstock');
  const [statuses, setStatuses] = useState<Record<DataSource, CrawlStatus>>({
    roofstock: { source: 'roofstock', status: 'idle' },
    rentcast: { source: 'rentcast', status: 'idle' },
    zillow: { source: 'zillow', status: 'idle' },
    zumper: { source: 'zumper', status: 'idle' },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCrawlStatuses();
    const interval = setInterval(loadCrawlStatuses, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadCrawlStatuses = async () => {
    try {
      const response = await api.getBatchCrawlStatus();
      if (response) {
        // Map the API response to our status format
        const mapStats = (data: { pending?: number; success?: number; completed?: number; error?: number; failed?: number; total: number }) => ({
          total: data.total,
          pending: data.pending || 0,
          completed: (data.completed || 0) + (data.success || 0),
          failed: (data.failed || 0) + (data.error || 0),
        });

        setStatuses(prev => ({
          ...prev,
          roofstock: {
            ...prev.roofstock,
            stats: response.roofstock ? mapStats(response.roofstock) : undefined,
          },
          rentcast: {
            ...prev.rentcast,
            stats: response.rentcast ? mapStats(response.rentcast) : undefined,
          },
          zillow: {
            ...prev.zillow,
            stats: response.zillow ? mapStats(response.zillow) : undefined,
          },
          zumper: {
            ...prev.zumper,
            stats: response.zumper ? mapStats(response.zumper) : undefined,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to load crawl statuses:', err);
    } finally {
      setLoading(false);
    }
  };

  const sources: { id: DataSource; label: string; icon: string; description: string }[] = [
    {
      id: 'roofstock',
      label: 'Roofstock',
      icon: '🏠',
      description: 'Investment property listings with turnkey data',
    },
    {
      id: 'rentcast',
      label: 'RentCast',
      icon: '📊',
      description: 'Market data, DOM, listings, rent history by ZIP',
    },
    {
      id: 'zillow',
      label: 'Zillow Market',
      icon: '📈',
      description: 'ZIP-level rental data and market trends',
    },
    {
      id: 'zumper',
      label: 'Zumper',
      icon: '🏢',
      description: 'Rental listings and demographic data',
    },
  ];

  if (loading) {
    return <Loading message="Loading crawl status..." />;
  }

  return (
    <div className="container crawl-page">
      <div className="crawl-header">
        <h1>Data Crawls</h1>
        <p className="crawl-subtitle">Trigger and monitor data source crawls</p>
      </div>

      {/* Source Tabs */}
      <div className="source-tabs">
        {sources.map(source => (
          <button
            key={source.id}
            className={`source-tab ${activeSource === source.id ? 'active' : ''}`}
            onClick={() => setActiveSource(source.id)}
          >
            <span className="source-icon">{source.icon}</span>
            <span className="source-label">{source.label}</span>
            {statuses[source.id].stats && (
              <span className="source-badge">
                {statuses[source.id].stats?.completed || 0}/{statuses[source.id].stats?.total || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Active Source Panel */}
      <div className="source-panel">
        {activeSource === 'roofstock' && (
          <RoofstockPanel status={statuses.roofstock} onRefresh={loadCrawlStatuses} />
        )}
        {activeSource === 'rentcast' && (
          <RentCastPanel status={statuses.rentcast} onRefresh={loadCrawlStatuses} />
        )}
        {activeSource === 'zillow' && (
          <ZillowPanel status={statuses.zillow} onRefresh={loadCrawlStatuses} />
        )}
        {activeSource === 'zumper' && (
          <ZumperPanel status={statuses.zumper} onRefresh={loadCrawlStatuses} />
        )}
      </div>
    </div>
  );
}

interface PanelProps {
  status: CrawlStatus;
  onRefresh: () => void;
}

interface CrawlCommandResult {
  success: boolean;
  message: string;
  command?: string;
  note?: string;
}

function RoofstockPanel({ status, onRefresh }: PanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrawlCommandResult | null>(null);
  const [state, setState] = useState('');
  const [reset, setReset] = useState(false);

  const handleCrawl = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.triggerRoofstockCrawl({
        state: state || undefined,
        reset,
      }) as unknown as CrawlCommandResult;
      setResult(response);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start crawl');
    } finally {
      setLoading(false);
    }
  };

  const copyCommand = () => {
    if (result?.command) {
      navigator.clipboard.writeText(result.command);
    }
  };

  const progress = status.stats
    ? Math.round(((status.stats.completed || 0) / (status.stats.total || 1)) * 100)
    : 0;

  return (
    <div className="panel-content">
      <div className="panel-header">
        <h2>🏠 Roofstock Properties</h2>
        <p>Crawl investment property listings from Roofstock's GeoJSON API using geographic bounding boxes.</p>
      </div>

      {status.stats && (
        <div className="crawl-progress-section">
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-value">{status.stats.total || 0}</span>
              <span className="stat-label">Total Boxes</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{status.stats.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{status.stats.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item error">
              <span className="stat-value">{status.stats.failed || 0}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-percent">{progress}%</span>
          </div>
        </div>
      )}

      <div className="crawl-form-section">
        <div className="form-group">
          <label className="form-label">State Filter (optional)</label>
          <select
            className="form-select"
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">All States</option>
            <option value="AL">Alabama</option>
            <option value="AZ">Arizona</option>
            <option value="FL">Florida</option>
            <option value="GA">Georgia</option>
            <option value="IL">Illinois</option>
            <option value="IN">Indiana</option>
            <option value="LA">Louisiana</option>
            <option value="MI">Michigan</option>
            <option value="MO">Missouri</option>
            <option value="MS">Mississippi</option>
            <option value="NC">North Carolina</option>
            <option value="OH">Ohio</option>
            <option value="OK">Oklahoma</option>
            <option value="PA">Pennsylvania</option>
            <option value="SC">South Carolina</option>
            <option value="TN">Tennessee</option>
            <option value="TX">Texas</option>
          </select>
          <span className="form-hint">Leave empty to crawl all states</span>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={reset}
              onChange={() => setReset(!reset)}
            />
            Reset crawl status (start fresh)
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        {result && (
          <div className="command-result">
            <div className="success-message">{result.message}</div>
            {result.command && (
              <div className="command-box">
                <code>{result.command}</code>
                <button className="btn-copy" onClick={copyCommand}>Copy</button>
              </div>
            )}
            {result.note && <p className="command-note">{result.note}</p>}
          </div>
        )}

        <button
          className="btn btn-primary btn-crawl"
          onClick={handleCrawl}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate CLI Command'}
        </button>
      </div>

      <div className="info-section">
        <h4>About Roofstock Data</h4>
        <ul>
          <li><strong>Property listings</strong> - Address, price, beds/baths, sqft</li>
          <li><strong>Investment metrics</strong> - Rent estimates, cap rate, cash flow</li>
          <li><strong>Images</strong> - Downloaded to R2 storage</li>
          <li><strong>Geographic coverage</strong> - Uses adaptive subdivision for dense areas</li>
        </ul>
      </div>
    </div>
  );
}

function RentCastPanel({ status, onRefresh }: PanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrawlCommandResult | null>(null);
  const [bearerToken, setBearerToken] = useState('');
  const [limit, setLimit] = useState('');
  const [retryErrors, setRetryErrors] = useState(false);

  const handleCrawl = async () => {
    if (!bearerToken.trim()) {
      setError('Bearer token is required');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.triggerRentCastCrawl({
        bearerToken: bearerToken.trim(),
        limit: limit ? parseInt(limit, 10) : undefined,
        retryErrors,
      }) as unknown as CrawlCommandResult;
      setResult(response);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start crawl');
    } finally {
      setLoading(false);
    }
  };

  const copyCommand = () => {
    if (result?.command) {
      navigator.clipboard.writeText(result.command);
    }
  };

  const progress = status.stats
    ? Math.round(((status.stats.completed || 0) / (status.stats.total || 1)) * 100)
    : 0;

  return (
    <div className="panel-content">
      <div className="panel-header">
        <h2>📊 RentCast Market Data</h2>
        <p>Fetch market data (DOM, listings, rent history) for all ZIPs.</p>
      </div>

      {status.stats && (
        <div className="crawl-progress-section">
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-value">{status.stats.total || 0}</span>
              <span className="stat-label">Total ZIPs</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{status.stats.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{status.stats.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item error">
              <span className="stat-value">{status.stats.failed || 0}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-percent">{progress}%</span>
          </div>
        </div>
      )}

      <div className="crawl-form-section">
        <div className="form-group">
          <label className="form-label required">Bearer Token</label>
          <input
            type="password"
            className="form-input"
            placeholder="Paste RentCast JWT token here..."
            value={bearerToken}
            onChange={(e) => setBearerToken(e.target.value)}
          />
          <span className="form-hint">
            Get token from browser DevTools (Network tab) after logging into rentcast.io. Tokens expire after ~1 hour.
          </span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Limit (optional)</label>
            <input
              type="number"
              className="form-input"
              placeholder="e.g., 100"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min={1}
            />
            <span className="form-hint">Max ZIPs to process</span>
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={retryErrors}
              onChange={() => setRetryErrors(!retryErrors)}
            />
            Retry failed ZIPs
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        {result && (
          <div className="command-result">
            <div className="success-message">{result.message}</div>
            {result.command && (
              <div className="command-box">
                <code>{result.command}</code>
                <button className="btn-copy" onClick={copyCommand}>Copy</button>
              </div>
            )}
            {result.note && <p className="command-note">{result.note}</p>}
          </div>
        )}

        <button
          className="btn btn-primary btn-crawl"
          onClick={handleCrawl}
          disabled={loading || !bearerToken.trim()}
        >
          {loading ? 'Generating...' : 'Generate CLI Command'}
        </button>
      </div>

      <div className="info-section">
        <h4>About RentCast Data</h4>
        <ul>
          <li><strong>Market metrics</strong> - Days on market, listing counts</li>
          <li><strong>Rent history</strong> - Historical rent trends by ZIP</li>
          <li><strong>Property types</strong> - SFR, MFR, condo breakdown</li>
          <li><strong>Bedroom data</strong> - Rent by bedroom count</li>
        </ul>
      </div>
    </div>
  );
}

function ZillowPanel({ status, onRefresh }: PanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrawlCommandResult | null>(null);
  const [limit, setLimit] = useState('');
  const [retryErrors, setRetryErrors] = useState(false);

  const handleCrawl = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.triggerZillowCrawl({
        limit: limit ? parseInt(limit, 10) : undefined,
        retryErrors,
      }) as unknown as CrawlCommandResult;
      setResult(response);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start crawl');
    } finally {
      setLoading(false);
    }
  };

  const copyCommand = () => {
    if (result?.command) {
      navigator.clipboard.writeText(result.command);
    }
  };

  const progress = status.stats
    ? Math.round(((status.stats.completed || 0) / (status.stats.total || 1)) * 100)
    : 0;

  return (
    <div className="panel-content">
      <div className="panel-header">
        <h2>📈 Zillow Market Data</h2>
        <p>Fetch rental data for all ZIPs via Firecrawl scraping.</p>
      </div>

      {status.stats && (
        <div className="crawl-progress-section">
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-value">{status.stats.total || 0}</span>
              <span className="stat-label">Total ZIPs</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{status.stats.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{status.stats.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item error">
              <span className="stat-value">{status.stats.failed || 0}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-percent">{progress}%</span>
          </div>
        </div>
      )}

      <div className="crawl-form-section">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Limit (optional)</label>
            <input
              type="number"
              className="form-input"
              placeholder="e.g., 100"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min={1}
            />
            <span className="form-hint">Max ZIPs to process</span>
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={retryErrors}
              onChange={() => setRetryErrors(!retryErrors)}
            />
            Retry failed ZIPs
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        {result && (
          <div className="command-result">
            <div className="success-message">{result.message}</div>
            {result.command && (
              <div className="command-box">
                <code>{result.command}</code>
                <button className="btn-copy" onClick={copyCommand}>Copy</button>
              </div>
            )}
            {result.note && <p className="command-note">{result.note}</p>}
          </div>
        )}

        <button
          className="btn btn-primary btn-crawl"
          onClick={handleCrawl}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate CLI Command'}
        </button>
      </div>

      <div className="info-section">
        <h4>About Zillow Data</h4>
        <ul>
          <li><strong>ZHVI</strong> - Zillow Home Value Index by ZIP</li>
          <li><strong>ZORI</strong> - Zillow Observed Rent Index</li>
          <li><strong>Forecasts</strong> - Home value appreciation predictions</li>
          <li><strong>Market heat</strong> - Supply/demand indicators</li>
        </ul>
      </div>
    </div>
  );
}

function ZumperPanel({ status, onRefresh }: PanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CrawlCommandResult | null>(null);
  const [limit, setLimit] = useState('');
  const [retryErrors, setRetryErrors] = useState(false);

  const handleCrawl = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.triggerZumperCrawl({
        limit: limit ? parseInt(limit, 10) : undefined,
        retryErrors,
      }) as unknown as CrawlCommandResult;
      setResult(response);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start crawl');
    } finally {
      setLoading(false);
    }
  };

  const copyCommand = () => {
    if (result?.command) {
      navigator.clipboard.writeText(result.command);
    }
  };

  const progress = status.stats
    ? Math.round(((status.stats.completed || 0) / (status.stats.total || 1)) * 100)
    : 0;

  return (
    <div className="panel-content">
      <div className="panel-header">
        <h2>🏢 Zumper Rentals</h2>
        <p>Fetch rental listings and demographic data via Apify scraper.</p>
      </div>

      {status.stats && (
        <div className="crawl-progress-section">
          <div className="progress-stats">
            <div className="stat-item">
              <span className="stat-value">{status.stats.total || 0}</span>
              <span className="stat-label">Total ZIPs</span>
            </div>
            <div className="stat-item success">
              <span className="stat-value">{status.stats.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-item warning">
              <span className="stat-value">{status.stats.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item error">
              <span className="stat-value">{status.stats.failed || 0}</span>
              <span className="stat-label">Failed</span>
            </div>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-percent">{progress}%</span>
          </div>
        </div>
      )}

      <div className="crawl-form-section">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Limit (optional)</label>
            <input
              type="number"
              className="form-input"
              placeholder="e.g., 100"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min={1}
            />
            <span className="form-hint">Max ZIPs to process</span>
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={retryErrors}
              onChange={() => setRetryErrors(!retryErrors)}
            />
            Retry failed ZIPs
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        {result && (
          <div className="command-result">
            <div className="success-message">{result.message}</div>
            {result.command && (
              <div className="command-box">
                <code>{result.command}</code>
                <button className="btn-copy" onClick={copyCommand}>Copy</button>
              </div>
            )}
            {result.note && <p className="command-note">{result.note}</p>}
          </div>
        )}

        <button
          className="btn btn-primary btn-crawl"
          onClick={handleCrawl}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate CLI Command'}
        </button>
      </div>

      <div className="info-section">
        <h4>About Zumper Data</h4>
        <ul>
          <li><strong>Rental listings</strong> - Current rental prices by ZIP</li>
          <li><strong>Demographics</strong> - Population, income, education</li>
          <li><strong>Employment</strong> - Job market data</li>
          <li><strong>Property types</strong> - Apartment, house, condo breakdown</li>
        </ul>
      </div>
    </div>
  );
}
